import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatCard, Loader } from "@/components/common";
import { money } from "@/lib/format";
import { Card } from "@/components/ui/card";
import {
  House, IdentificationCard, Scroll, Receipt, Money as MoneyIcon,
  Warning, HandCoins, Buildings, ChartBar, TrendUp,
} from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell } from "recharts";

export default function Dashboard() {
  const { user, company } = useAuth();
  const [d, setD] = useState(null);
  const cur = company?.currency || "KES";

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => setD({}));
  }, []);

  if (!d) return <Loader />;

  if (user.role === "super_admin") {
    return (
      <div>
        <PageHeader title={`Welcome, ${user.name.split(" ")[0]}`} subtitle="Organization-wide overview" icon={ChartBar} />
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard label="Companies" value={d.companies ?? 0} icon={Buildings} testId="stat-companies" />
          <StatCard label="Property Managers" value={d.property_managers ?? 0} icon={IdentificationCard} accent="sky" testId="stat-pms" />
          <StatCard label="Total Properties" value={d.total_properties ?? 0} icon={House} accent="ochre" testId="stat-props" />
          <StatCard label="Total Tenants" value={d.total_tenants ?? 0} icon={IdentificationCard} testId="stat-tenants" />
          <StatCard label="Rent Collected" value={money(d.total_collected, cur)} icon={Receipt} accent="terracotta" testId="stat-collected" />
          <StatCard label="Management Fees" value={money(d.management_fees, cur)} icon={MoneyIcon} accent="sky" testId="stat-fees" />
        </div>
      </div>
    );
  }

  const occ = [
    { name: "Occupied", value: d.occupied || 0, color: "#2D4A22" },
    { name: "Vacant", value: d.vacant || 0, color: "#D69F3A" },
  ];
  const bars = [
    { name: "Billed", amount: d.total_billed || 0 },
    { name: "Collected", amount: d.total_collected || 0 },
    { name: "Arrears", amount: d.arrears || 0 },
    { name: "Mgmt Fees", amount: d.management_fees || 0 },
  ];

  return (
    <div>
      <PageHeader title={`Welcome, ${user.name.split(" ")[0]}`} subtitle={company?.name || "Your dashboard"} icon={ChartBar} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Properties" value={d.total_properties ?? 0} icon={House} testId="stat-props" />
        <StatCard label="Occupied" value={d.occupied ?? 0} icon={House} accent="sky" testId="stat-occupied" />
        <StatCard label="Vacant" value={d.vacant ?? 0} icon={House} accent="ochre" testId="stat-vacant" />
        <StatCard label="Tenants" value={d.total_tenants ?? 0} icon={IdentificationCard} testId="stat-tenants" />
        <StatCard label="Active Leases" value={d.active_leases ?? 0} icon={Scroll} testId="stat-leases" />
        <StatCard label="Collected" value={money(d.total_collected, cur)} icon={Receipt} accent="sky" testId="stat-collected" />
        <StatCard label="Arrears" value={money(d.arrears, cur)} icon={Warning} accent="terracotta" testId="stat-arrears" />
        <StatCard label="Pending Commissions" value={d.pending_commissions ?? 0} icon={HandCoins} accent="ochre" testId="stat-commissions" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <Card className="p-5 lg:col-span-2">
          <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2"><TrendUp size={20} weight="duotone" className="text-primary" /> Financial Summary</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={bars}>
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} width={70} />
              <Tooltip formatter={(v) => money(v, cur)} />
              <Bar dataKey="amount" fill="#2D4A22" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5">
          <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2"><House size={20} weight="duotone" className="text-primary" /> Occupancy</h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={occ} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                {occ.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 text-sm">
            {occ.map((e) => (
              <span key={e.name} className="flex items-center gap-1.5">
                <span className="h-3 w-3 rounded-full" style={{ background: e.color }} /> {e.name}
              </span>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
